import { Fragment, useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    getUserReceipts,
    getTransactionsWithoutReceipts,
    uploadUserReceipt,
    deleteReceipt,
    fetchReceipt,
} from "../../services/TransactionService";
import useDebouncedValue from '../../hooks/useDebouncedValue';
import useGuidedTour from '../../hooks/useGuidedTour';
import { showMutationError } from '../../utils/showMutationError';
import {
    MY_RECEIPTS_OVERVIEW_TOUR_ID,
    buildMyReceiptsOverviewTourSteps,
} from './MyReceiptsMainTable.tour';

const statusBadgeClass = (status) => {
    const s = status?.toLowerCase();
    if (s === 'approved') return 'badge-success';
    if (s === 'pending')  return 'badge-warning';
    return 'badge-danger';
};

const PaginationBar = ({ page, lastPage, isLoading, onPrev, onNext }) => {
    if (lastPage <= 1) return null;
    return (
        <div className="d-flex justify-content-center align-items-center gap-3 px-3 py-2 border-top">
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page <= 1 || isLoading}
                onClick={onPrev}
                aria-label="Previous page"
            >
                <i className="fa fa-angle-left me-1" />
                Prev
            </button>
            <span className="text-muted small">Page {page} of {lastPage}</span>
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page >= lastPage || isLoading}
                onClick={onNext}
                aria-label="Next page"
            >
                Next
                <i className="fa fa-angle-right ms-1" />
            </button>
        </div>
    );
};

const MyReceiptsMainTable = () => {
    const queryClient = useQueryClient();
    const authUser  = useSelector(state => state.auth.authUser);
    const partnerId = authUser?.partner_id;

    useGuidedTour(MY_RECEIPTS_OVERVIEW_TOUR_ID, buildMyReceiptsOverviewTourSteps());

    const [currentPage,        setCurrentPage]        = useState(1);
    const cardRef                                     = useRef(null);

    const goToPage = (nextPage) => {
        setCurrentPage(nextPage);
        if (cardRef.current) {
            cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };
    const [dateFrom,           setDateFrom]           = useState('');
    const [dateTo,             setDateTo]             = useState('');
    const [search,             setSearch]             = useState('');
    const [viewingReceipt,     setViewingReceipt]     = useState(null);
    const [receiptToDelete,    setReceiptToDelete]    = useState(null);
    const [showAddModal,       setShowAddModal]       = useState(false);
    const [newReceiptFormData, setNewReceiptFormData] = useState({ image: null, transactionId: '' });
    const [receiptUrl,         setReceiptUrl]         = useState('');
    const [receiptMime,        setReceiptMime]        = useState('');

    const debouncedSearch = useDebouncedValue(search, 300);

    const receiptsQuery = useQuery({
        queryKey: ['user-receipts', partnerId, authUser?.id, currentPage, debouncedSearch, dateFrom, dateTo],
        queryFn:  () => getUserReceipts(partnerId, authUser.id, {
            page:     currentPage,
            search:   debouncedSearch,
            dateFrom,
            dateTo,
        }).then(r => r.data),
        enabled:         !!partnerId && !!authUser?.id,
        placeholderData: keepPreviousData,
        staleTime:       0,
    });

    const txWithoutReceiptsQuery = useQuery({
        queryKey: ['transactions-without-receipts', authUser?.id],
        queryFn:  () => getTransactionsWithoutReceipts(authUser.id).then(r => r.data.data),
        enabled:  !!authUser?.id,
        staleTime: 0,
    });

    const userReceipts                = receiptsQuery.data?.data ?? [];
    const totalPages                  = receiptsQuery.data?.meta?.last_page ?? 1;
    const transactionsWithoutReceipts = txWithoutReceiptsQuery.data ?? [];

    const handleFilterChange = (setter) => (e) => {
        setter(e.target.value);
        setCurrentPage(1);
    };

    const hasActiveFilter = dateFrom !== '' || dateTo !== '' || search !== '';

    const handleClear = () => {
        setDateFrom('');
        setDateTo('');
        setSearch('');
        setCurrentPage(1);
    };

    const uploadMutation = useMutation({
        mutationFn: ({ partnerId, userId, formData }) => uploadUserReceipt(partnerId, userId, formData),
        onSuccess: () => {
            toast.success('Your receipt has been uploaded successfully.');
            queryClient.invalidateQueries({ queryKey: ['user-receipts', partnerId, authUser.id] });
            queryClient.invalidateQueries({ queryKey: ['transactions-without-receipts', authUser.id] });
            setNewReceiptFormData({ image: null, transactionId: '' });
            setShowAddModal(false);
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message || 'Upload failed. Please try again later.');
        },
    });

    const handleUploadNewReceipt = () => {
        if (!newReceiptFormData.transactionId || !newReceiptFormData.image) return;
        const formData = new FormData();
        formData.append('user_payout_id', newReceiptFormData.transactionId);
        formData.append('receipt', newReceiptFormData.image);
        uploadMutation.mutate({ partnerId, userId: authUser.id, formData });
    };

    const { data: receiptBlob, isPending: receiptIsLoading, isError: receiptFetchFailed } = useQuery({
        queryKey: ['receiptData', viewingReceipt?.id],
        queryFn:  () => fetchReceipt(partnerId, authUser.id, viewingReceipt.id),
        staleTime: Infinity,
        enabled:  !!viewingReceipt,
        retry:    1,
    });

    const [receiptImageBroken, setReceiptImageBroken] = useState(false);

    useEffect(() => { setReceiptImageBroken(false); }, [viewingReceipt?.id]);

    useEffect(() => {
        if (receiptBlob) {
            const url = URL.createObjectURL(receiptBlob);
            setReceiptUrl(url);
            setReceiptMime(receiptBlob.type || '');
            return () => {
                URL.revokeObjectURL(url);
                setReceiptUrl('');
                setReceiptMime('');
            };
        }
    }, [receiptBlob]);

    const { mutate: mutateDelete, isPending: isPendingDelete } = useMutation({
        mutationFn: (id) => deleteReceipt(authUser.id, id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['user-receipts', partnerId, authUser.id] });
            queryClient.invalidateQueries({ queryKey: ['transactions-without-receipts', authUser.id] });
            toast.success('Receipt deleted successfully!');
            setReceiptToDelete(null);
        },
        onError: (err) => showMutationError(err, 'Receipt was not deleted. Please try again later.'),
    });

    return (
        <Fragment>
            <div className="row">
                <div className="col-12">
                    <div className="card" ref={cardRef} data-tour="my-receipts-card">
                        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h4 className="card-title mb-0">My Receipts</h4>
                            <button
                                className="btn btn-sm btn-primary"
                                onClick={() => setShowAddModal(true)}
                                disabled={transactionsWithoutReceipts.length === 0}
                                title={transactionsWithoutReceipts.length === 0
                                    ? 'No successful payments are waiting for a receipt right now.'
                                    : undefined}
                                data-tour="my-receipts-add-button"
                            >
                                Add Receipt
                            </button>
                        </div>
                        <div className="card-body">
                            {(() => {
                                const fromLabel = dateFrom ? `From ${dateFrom}` : 'From date';
                                const toLabel   = dateTo   ? `To ${dateTo}`     : 'To date';
                                return (
                                    <div className="mb-3" data-tour="my-receipts-filters">
                                        <input
                                            type="text"
                                            className="form-control mb-2"
                                            placeholder="Search"
                                            value={search}
                                            onChange={handleFilterChange(setSearch)}
                                        />
                                        <div className="d-flex flex-wrap gap-2 align-items-center">
                                            <Dropdown autoClose="outside">
                                                <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                                                    <span className="flex-grow-1 text-start">{fromLabel}</span>
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu className="p-2">
                                                    <input
                                                        type="date"
                                                        className="form-control form-control-sm"
                                                        value={dateFrom}
                                                        onChange={handleFilterChange(setDateFrom)}
                                                    />
                                                </Dropdown.Menu>
                                            </Dropdown>
                                            <Dropdown autoClose="outside">
                                                <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                                                    <span className="flex-grow-1 text-start">{toLabel}</span>
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu className="p-2">
                                                    <input
                                                        type="date"
                                                        className="form-control form-control-sm"
                                                        value={dateTo}
                                                        onChange={handleFilterChange(setDateTo)}
                                                    />
                                                </Dropdown.Menu>
                                            </Dropdown>
                                            {hasActiveFilter && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link text-decoration-none p-0 ms-1"
                                                    onClick={handleClear}
                                                >
                                                    Clear
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })()}
                            {receiptsQuery.isPending ? (
                                <div className="d-flex justify-content-center py-4">
                                    <span className="spinner-border spinner-border-sm text-primary" role="status" />
                                </div>
                            ) : receiptsQuery.error ? (
                                <p className="text-center text-danger py-4 mb-0">
                                    Failed to load your receipts. Please try again.
                                </p>
                            ) : userReceipts.length === 0 ? (
                                <p className="text-center text-muted py-4 mb-0">
                                    {hasActiveFilter
                                        ? 'No receipts match your filters.'
                                        : transactionsWithoutReceipts.length === 0
                                            ? 'You have not attached any receipts yet. Make a successful payment first, then come back to attach the receipt.'
                                            : 'You have not attached any receipts yet. Click Add Receipt to attach one.'}
                                </p>
                            ) : (() => {
                                const rowActions = (item) => (
                                    <div className="d-inline-flex gap-2">
                                        <button
                                            className="btn btn-sm btn-outline-info"
                                            onClick={() => setViewingReceipt(item)}
                                            aria-label="View receipt"
                                        >
                                            <i className="fa fa-eye" aria-hidden="true" />
                                        </button>
                                        {item.status_slug === 'pending' && (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => setReceiptToDelete(item)}
                                                aria-label="Delete receipt"
                                            >
                                                <i className="fa fa-trash" aria-hidden="true" />
                                            </button>
                                        )}
                                    </div>
                                );
                                return (
                                    <div data-tour="my-receipts-list">
                                        <div className="table-responsive d-none d-xl-block">
                                            <table className="table verticle-middle mb-0">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">Expense Tags</th>
                                                        <th scope="col">Amount</th>
                                                        <th scope="col">Date</th>
                                                        <th scope="col">MPESA Code</th>
                                                        <th scope="col">Status</th>
                                                        <th scope="col">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {userReceipts.map((item) => (
                                                        <tr key={item.id}>
                                                            <td style={{ overflowWrap: 'anywhere' }}>{item.tags?.join(', ') || '-'}</td>
                                                            <td className="text-nowrap">{item.amount}</td>
                                                            <td className="text-nowrap">{item.date}</td>
                                                            <td>{item.mpesa_transaction_code}</td>
                                                            <td>
                                                                <span className={`badge badge-rounded ${statusBadgeClass(item.status)}`}>
                                                                    {item.status}
                                                                </span>
                                                            </td>
                                                            <td>{rowActions(item)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="table-responsive d-none d-md-block d-xl-none">
                                            <table className="table verticle-middle mb-0">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">Receipt</th>
                                                        <th scope="col">Status</th>
                                                        <th scope="col" className="text-end">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {userReceipts.map((item) => (
                                                        <tr key={item.id} className="align-top">
                                                            <td>
                                                                <div style={{ overflowWrap: 'anywhere' }}>{item.tags?.join(', ') || '-'}</div>
                                                                <div className="fw-semibold mt-1">{item.amount}</div>
                                                                <div className="text-muted small">{item.date}</div>
                                                                <div className="text-muted small">MPESA: {item.mpesa_transaction_code}</div>
                                                            </td>
                                                            <td>
                                                                <span className={`badge badge-rounded ${statusBadgeClass(item.status)}`}>
                                                                    {item.status}
                                                                </span>
                                                            </td>
                                                            <td className="text-end">{rowActions(item)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <ul className="list-group list-group-flush d-block d-md-none">
                                            {userReceipts.map((item) => (
                                                <li key={item.id} className="list-group-item py-3">
                                                    <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                                                        <div className="d-flex align-items-center flex-wrap gap-2" style={{ minWidth: 0 }}>
                                                            <span className={item.tags?.length ? 'text-body' : 'text-muted fst-italic'} style={{ overflowWrap: 'anywhere' }}>
                                                                {item.tags?.length ? item.tags.join(', ') : 'Untagged'}
                                                            </span>
                                                            <span className={`badge badge-rounded ${statusBadgeClass(item.status)}`}>
                                                                {item.status}
                                                            </span>
                                                        </div>
                                                        <div className="flex-shrink-0">{rowActions(item)}</div>
                                                    </div>
                                                    <div className="fs-5 fw-semibold text-body">{item.amount}</div>
                                                    <div className="text-body-secondary fw-semibold mt-1" style={{ overflowWrap: 'anywhere', letterSpacing: '0.08em' }}>
                                                        {item.mpesa_transaction_code}
                                                    </div>
                                                    <div className="small text-muted mt-1">{item.date}</div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                );
                            })()}
                            <PaginationBar
                                page={currentPage}
                                lastPage={totalPages}
                                isLoading={receiptsQuery.isFetching}
                                onPrev={() => goToPage(Math.max(currentPage - 1, 1))}
                                onNext={() => goToPage(Math.min(currentPage + 1, totalPages))}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={!!receiptToDelete} onHide={() => setReceiptToDelete(null)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Delete this receipt?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {receiptToDelete && (
                        <p className="mb-0">
                            Delete the receipt for <strong>{receiptToDelete.amount}</strong> dated <strong>{receiptToDelete.date}</strong>? This cannot be undone.
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setReceiptToDelete(null)} disabled={isPendingDelete}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => receiptToDelete && mutateDelete(receiptToDelete.id)}
                        disabled={isPendingDelete}
                    >
                        {isPendingDelete ? (
                            <>
                                <i className="fa fa-spinner fa-spin me-1" aria-hidden="true" />
                                Deleting...
                            </>
                        ) : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!viewingReceipt} onHide={() => setViewingReceipt(null)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Receipt Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {viewingReceipt && (
                        <div className="text-center mb-3">
                            <div className="fs-3 fw-bold text-primary">{viewingReceipt.amount}</div>
                            <div className="mt-1">
                                <span className={`badge badge-rounded ${statusBadgeClass(viewingReceipt.status)}`}>
                                    {viewingReceipt.status}
                                </span>
                            </div>
                        </div>
                    )}

                    <div className="mb-3">
                        {receiptIsLoading ? (
                            <div className="d-flex flex-column align-items-center justify-content-center bg-light rounded p-4" style={{ minHeight: 220 }}>
                                <span className="spinner-border spinner-border-sm text-primary" role="status" />
                                <div className="text-muted small mt-2">Loading the receipt file…</div>
                            </div>
                        ) : (receiptFetchFailed || !receiptUrl || receiptImageBroken) ? (
                            <div className="d-flex flex-column align-items-center justify-content-center bg-light rounded p-4 text-center" style={{ minHeight: 220 }}>
                                <i className="fa fa-file-image-o fa-3x text-muted mb-2" aria-hidden="true" />
                                <div className="text-muted">The receipt file could not be loaded.</div>
                                <div className="text-muted small mt-1">Try closing this and opening it again. If it keeps failing, re-upload the receipt from the row.</div>
                            </div>
                        ) : receiptMime === 'application/pdf' ? (
                            <div>
                                <iframe
                                    src={receiptUrl}
                                    title="Receipt PDF"
                                    className="w-100 rounded border"
                                    style={{ height: '400px' }}
                                />
                                <div className="mt-2 text-center">
                                    <a href={receiptUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary">
                                        Open PDF in a new tab
                                    </a>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center">
                                <img
                                    src={receiptUrl}
                                    alt="Receipt"
                                    className="img-fluid rounded border"
                                    style={{ maxHeight: '400px' }}
                                    onError={() => setReceiptImageBroken(true)}
                                />
                            </div>
                        )}
                    </div>

                    {viewingReceipt && (
                        <dl className="row mb-0">
                            <dt className="col-4 col-md-3 text-muted fw-normal">Tags</dt>
                            <dd className="col-8 col-md-9 mb-2">
                                {viewingReceipt.tags?.length
                                    ? viewingReceipt.tags.join(', ')
                                    : <span className="text-muted fst-italic">Untagged</span>}
                            </dd>
                            <dt className="col-4 col-md-3 text-muted fw-normal">Date</dt>
                            <dd className="col-8 col-md-9 mb-2">{viewingReceipt.date}</dd>
                            <dt className="col-4 col-md-3 text-muted fw-normal">MPESA code</dt>
                            <dd className="col-8 col-md-9 mb-2 fw-semibold" style={{ letterSpacing: '0.08em', overflowWrap: 'anywhere' }}>
                                {viewingReceipt.mpesa_transaction_code}
                            </dd>
                            {viewingReceipt.status_slug === 'rejected' && viewingReceipt.reject_reason && (
                                <>
                                    <dt className="col-4 col-md-3 text-danger fw-normal">Reason for rejection</dt>
                                    <dd className="col-8 col-md-9 mb-0 text-danger" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                        {viewingReceipt.reject_reason}
                                    </dd>
                                </>
                            )}
                        </dl>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setViewingReceipt(null)}>Close</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>New Receipt</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="new-receipt-transaction" className="form-label">Transaction</label>
                        <select
                            id="new-receipt-transaction"
                            className="form-select"
                            value={newReceiptFormData.transactionId || ''}
                            onChange={(e) => setNewReceiptFormData({ ...newReceiptFormData, transactionId: e.target.value })}
                        >
                            <option value="">Select Transaction</option>
                            {transactionsWithoutReceipts.map(tx => (
                                <option key={tx.id} value={tx.id}>{tx.post_mpesa_receipt_number}</option>
                            ))}
                        </select>
                    </div>
                    <div className="mb-3">
                        <label htmlFor="new-receipt-file" className="form-label">Upload receipt (JPG, PNG or PDF, up to 2 MB)</label>
                        <input
                            id="new-receipt-file"
                            type="file"
                            className="form-control"
                            accept=".jpg,.jpeg,.png,.pdf"
                            onChange={(e) => setNewReceiptFormData({ ...newReceiptFormData, image: e.target.files[0] })}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={uploadMutation.isPending}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleUploadNewReceipt}
                        disabled={uploadMutation.isPending || !newReceiptFormData.transactionId || !newReceiptFormData.image}
                    >
                        {uploadMutation.isPending ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Uploading…
                            </>
                        ) : 'Submit'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

export default MyReceiptsMainTable;
